import { useApiAction } from '@bluheadless/ui-logic/src/hooks/action'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { postSalesPaymentInit } from 'data-api'
import { useStore } from '@bluheadless/ui-logic/src/providers/store'
import { PAGE_PATH_CHECKOUT } from '@bluheadless/ui-logic/src/constants'
import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { useScript } from '@bluheadless/ui-logic/src/hooks/use-script/useScript'
import Progress from '../../atoms/progress/progress'
import { Root } from './amazon-pay-button.styled'
import { CHECKOUT_TYPE_AMAZON_TWO_STEPS } from '@bluheadless/ui-logic/src/checkout/types'
import { keepOneSlash } from '@bluheadless/ui-logic/src/utils'

const buttonIdPrefix = 'AmazonPayButton'

const AmazonPayButton = (props) => {
	const {
		sales: {
			payments: {
				amazonpay: { enabled, js },
			},
		},
	} = useConfig()

	const randomString = useMemo(() => (Math.random() + 1).toString(36).substring(7), [])
	const buttonId = `${buttonIdPrefix}-${randomString}`
	const scriptStatus = useScript({ src: enabled && js })

	const containerRef = useRef()
	const { action: initPayment, loading: _loading } = useApiAction({ method: postSalesPaymentInit })
	const { baseUrl, path } = useStore()

	const returnUrl = useMemo(() => {
		const returnPath = keepOneSlash(`${path}${PAGE_PATH_CHECKOUT}`)
		const returnUrl = new URL(returnPath, baseUrl)
		returnUrl.search = new URLSearchParams({ type: CHECKOUT_TYPE_AMAZON_TWO_STEPS }).toString()
		return returnUrl
	}, [baseUrl, path])

	const initButton = useCallback(async () => {
		try {
			const data = await initPayment({ code: 'amazonpay', returnUrl })

			window.amazon.Pay.renderButton(`#${buttonId}`, {
				// set checkout environment
				merchantId: data.merchantId,
				publicKeyId: data.publicKeyId,
				ledgerCurrency: data.ledgerCurrency,
				// customize the buyer experience
				checkoutLanguage: data.checkoutLanguage,
				productType: data.productType,
				placement: data.placement,
				buttonColor: data.buttonColor,
				// configure Create Checkout Session request
				createCheckoutSessionConfig: data.createCheckoutSessionConfig,
				sandbox: data.sandbox,
			})
			return () => {
				const button = document.getElementById(buttonId)
				if (button?.shadowRoot?.innerHTML) {
					button.shadowRoot.innerHTML = ''
				}
			}
		} catch (e) {
			console.error(e)
		}
	}, [initPayment, returnUrl, buttonId])

	useEffect(() => {
		if (scriptStatus === 'ready') {
			initButton()
		}
	}, [scriptStatus, initButton])

	const loading = scriptStatus !== 'ready' || _loading

	return (
		enabled && (
			<Root ref={containerRef} id={buttonId} {...props}>
				{loading && <Progress />}
			</Root>
		)
	)
}

export default AmazonPayButton
